/**
 * AppScreenNode - Voice-Native App Screen Node
 *
 * A React Flow node that represents one screen in a voice-driven app.
 * Each screen contains widgets (Card, Button, Text, etc.) that the
 * voice agent can interact with.
 *
 * Design principles:
 * - Follows DesignerNode patterns (floating header, drag behavior)
 * - Purple/violet theme to differentiate from other nodes
 * - Voice indicator shows this is voice-enabled
 * - Home badge shows which screen is the entry point
 * - Edges to other AppScreenNodes = navigation
 * - Edges to agent nodes = trigger capabilities
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position, type NodeProps, type Node, type Edge } from '@xyflow/react';
import { useParams } from 'react-router-dom';
import type { AppScreenNodeData, AppWidget, AppSettings, ScreenVoiceConfig } from '@deep-agents/shared-types';
import { createAppWidget, createDefaultAppSettings } from '@deep-agents/shared-types';
import { WidgetCanvas } from './WidgetCanvas';
import { WidgetPalette } from './WidgetPalette';
import { AppSettingsModal, findConnectedScreens, findHomeScreen } from './AppSettingsModal';
import { PublishPanel } from './PublishPanel';
import type { AppScreenNodeRuntimeData } from './types';
import { DEFAULT_CANVAS_WIDTH, THEME_COLORS } from './constants';

// ─────────────────────────────────────────────────────────────
// Screen Voice Settings Modal
// ─────────────────────────────────────────────────────────────

/**
 * Modal to edit screen-level voice config:
 * - onEnterSpeak: What the agent says when navigating to this screen
 * - alias: How users can refer to this screen verbally
 */
function ScreenVoiceModal({
  isOpen,
  onClose,
  config,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  config?: ScreenVoiceConfig;
  onSave: (config: ScreenVoiceConfig) => void;
}) {
  const [onEnterSpeak, setOnEnterSpeak] = useState(config?.onEnterSpeak || '');
  const [alias, setAlias] = useState(config?.alias || '');

  // Sync local state when modal opens with new config
  useEffect(() => {
    if (isOpen) {
      setOnEnterSpeak(config?.onEnterSpeak || '');
      setAlias(config?.alias || '');
    }
  }, [isOpen, config]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        // Close when clicking the backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-xl p-6 w-96 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Screen Voice Settings
        </h3>

        <div className="space-y-4">
          {/* On Enter Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              On Enter Message
            </label>
            <textarea
              value={onEnterSpeak}
              onChange={(e) => setOnEnterSpeak(e.target.value)}
              placeholder="What should the agent say when entering this screen?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Example: "Here are your recommended products!"
            </p>
          </div>

          {/* Screen Alias */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Screen Alias
            </label>
            <input
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="e.g., 'the products page'"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              How users can refer to this screen verbally
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSave({
                onEnterSpeak: onEnterSpeak || undefined,
                alias: alias || undefined,
              });
              onClose();
            }}
            className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// Type for the React Flow node
type AppScreenNodeType = Node<AppScreenNodeData, 'appScreen'>;

export const AppScreenNode = memo(function AppScreenNode({
  id,
  data,
  selected,
  dragging,
}: NodeProps<AppScreenNodeType>) {
  // Get actual board ID from URL params (for API calls)
  const { boardId: urlBoardId } = useParams<{ boardId: string }>();

  // Destructure data with defaults
  const {
    title = 'New Screen',
    appId = '',
    isHomeScreen = false,
    widgets = [],
    voice,
    appSettings,
    nodesMap,
    edgesMap,
    makeChange,
  } = data as AppScreenNodeRuntimeData;

  // Debug: log widget count on each render
  console.log('[AppScreenNode] Render - widgets from data:', widgets.length, title);

  // Local state for title editing
  const [localTitle, setLocalTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPublishPanel, setShowPublishPanel] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // Get all nodes and edges as arrays for helper functions
  // NOTE: We intentionally DON'T use useMemo here because the nodesMap/edgesMap
  // references don't change when their contents change (they're Yjs Maps).
  // We need to read fresh values every render to get the latest data.
  const allNodes = nodesMap ? Array.from(nodesMap.values()) : [];
  const allEdges = edgesMap ? (Array.from(edgesMap.values()) as Edge[]) : [];

  // Debug: log what we're getting from nodesMap
  console.log('[AppScreenNode] allNodes from nodesMap:', allNodes.length, 'nodes');
  if (allNodes.length > 0) {
    const thisNode = allNodes.find(n => n.id === id);
    console.log('[AppScreenNode] This node in allNodes:', thisNode?.id, 'widgets:', (thisNode?.data as AppScreenNodeData)?.widgets?.length || 0);
  }

  // Find connected screens for the settings modal
  const connectedScreens = useMemo(() => {
    return findConnectedScreens(id, allNodes, allEdges);
  }, [id, allNodes, allEdges]);

  // Get the current app settings (from this node if home, or from home node)
  const currentSettings = useMemo((): AppSettings => {
    if (isHomeScreen && appSettings) {
      return appSettings;
    }
    // Find the home screen and get settings from there
    const homeNode = findHomeScreen(id, allNodes, allEdges);
    if (homeNode) {
      const homeData = homeNode.data as AppScreenNodeData;
      if (homeData.appSettings) {
        return homeData.appSettings;
      }
    }
    // Fall back to defaults
    return createDefaultAppSettings(title);
  }, [isHomeScreen, appSettings, id, allNodes, allEdges, title]);

  // Sync title with external changes
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Dynamic shadow based on state
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // Show controls when hovered or selected
  const showControls = isHovered || selected;

  // Handle title change with Yjs persistence
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setLocalTitle(newTitle);

      if (!nodesMap || !makeChange) return;

      makeChange(() => {
        const existing = nodesMap.get(id);
        if (!existing) return;
        nodesMap.set(id, {
          ...existing,
          data: {
            ...existing.data,
            title: newTitle,
          },
        });
      });
    },
    [id, nodesMap, makeChange]
  );

  // Handle widgets change with Yjs persistence
  const handleWidgetsChange = useCallback(
    (newWidgets: AppWidget[]) => {
      if (!nodesMap || !makeChange) return;

      console.log('[AppScreenNode] Saving widgets:', newWidgets.length, newWidgets.map(w => ({ id: w.id, type: w.type })));

      makeChange(() => {
        const existing = nodesMap.get(id);
        if (!existing) return;
        nodesMap.set(id, {
          ...existing,
          data: {
            ...existing.data,
            widgets: newWidgets,
          },
        });
      });
    },
    [id, nodesMap, makeChange]
  );

  // Handle adding a new widget
  const handleAddWidget = useCallback(
    (type: Parameters<typeof createAppWidget>[0]) => {
      const newWidget = createAppWidget(type, widgets.length);
      handleWidgetsChange([...widgets, newWidget]);
    },
    [widgets, handleWidgetsChange]
  );

  // Toggle home screen status
  const handleToggleHome = useCallback(() => {
    if (!nodesMap || !makeChange) return;

    makeChange(() => {
      const existing = nodesMap.get(id);
      if (!existing) return;
      nodesMap.set(id, {
        ...existing,
        data: {
          ...existing.data,
          isHomeScreen: !isHomeScreen,
        },
      });
    });
  }, [id, isHomeScreen, nodesMap, makeChange]);

  // Save app settings (always to home screen)
  const handleSaveSettings = useCallback(
    (newSettings: AppSettings) => {
      if (!nodesMap || !makeChange) return;

      // Find the home screen to save settings to
      const homeScreen = findHomeScreen(id, allNodes, allEdges);
      const targetNodeId = homeScreen?.id || id;

      makeChange(() => {
        const existing = nodesMap.get(targetNodeId);
        if (!existing) return;
        nodesMap.set(targetNodeId, {
          ...existing,
          data: {
            ...existing.data,
            appSettings: newSettings,
          },
        });
      });
    },
    [id, allNodes, allEdges, nodesMap, makeChange]
  );

  // Open settings modal
  const handleOpenSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  // Save screen voice config to this node's data
  const handleSaveVoiceConfig = useCallback(
    (voiceConfig: ScreenVoiceConfig) => {
      if (!nodesMap || !makeChange) return;

      makeChange(() => {
        const existing = nodesMap.get(id);
        if (!existing) return;
        nodesMap.set(id, {
          ...existing,
          data: {
            ...existing.data,
            voice: voiceConfig,
          },
        });
      });
    },
    [id, nodesMap, makeChange]
  );

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px', width: DEFAULT_CANVAS_WIDTH + 16 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-violet-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Connect from another screen (navigation)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-violet-600 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Navigate to another screen or trigger agent"
      />

      {/* Floating header (above card, visible on hover/select) */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          z-30
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1">
          {/* Node type badge */}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-100/80 rounded-lg border border-violet-200/50">
            {/* Voice icon */}
            <svg
              className="w-3.5 h-3.5 text-violet-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
              />
            </svg>
            <span className="text-[11px] font-semibold text-violet-700">App Screen</span>
          </div>

          {/* Home screen toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleHome();
            }}
            className={`
              nodrag nopan px-2 py-1 rounded-lg text-[11px] font-medium transition-colors
              ${
                isHomeScreen
                  ? 'bg-amber-100 text-amber-700 border border-amber-200'
                  : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-amber-50'
              }
            `}
            title={isHomeScreen ? 'This is the home screen' : 'Set as home screen'}
          >
            🏠 {isHomeScreen ? 'Home' : 'Set Home'}
          </button>

          {/* Screen Voice Settings button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowVoiceModal(true);
            }}
            className={`
              nodrag nopan px-2 py-1 rounded-lg text-[11px] font-medium transition-colors border
              ${voice?.onEnterSpeak || voice?.alias
                ? 'bg-violet-100 text-violet-700 border-violet-200'
                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200'
              }
            `}
            title="Screen Voice Settings"
          >
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
              Voice
            </span>
          </button>

          {/* Settings button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenSettings();
            }}
            className="nodrag nopan px-2 py-1 rounded-lg text-[11px] font-medium transition-colors bg-gray-100 text-gray-600 border border-gray-200 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200"
            title="App Settings"
          >
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </span>
          </button>

          {/* Publish button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowPublishPanel(true);
            }}
            className="nodrag nopan px-2 py-1 rounded-lg text-[11px] font-medium transition-colors bg-violet-600 text-white border border-violet-700 hover:bg-violet-700"
            title="Publish App"
          >
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Publish
            </span>
          </button>

          {/* Title input */}
          <div
            className="nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Screen name"
              className="
                px-2.5 py-1 text-[12px] font-semibold text-violet-700
                bg-gradient-to-r from-violet-50 to-indigo-50
                rounded-lg border border-violet-100/50
                outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200
                placeholder:text-violet-300 placeholder:font-medium
                min-w-[80px] max-w-[180px] truncate
                transition-all
              "
              style={{ width: `${Math.max(80, Math.min(180, (localTitle.length || 12) * 7))}px` }}
            />
          </div>
        </div>
      </div>

      {/* Main card container */}
      <div
        className="
          relative bg-white rounded-2xl overflow-hidden
          border transition-all duration-300
          border-violet-200/60
        "
        style={{
          boxShadow: getShadowStyle(),
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
        }}
      >
        {/* Screen header bar */}
        <div
          className="px-4 py-2 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-violet-100"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-violet-800">{localTitle}</span>
            <div className="flex items-center gap-1">
              {isHomeScreen && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                  🏠 Home
                </span>
              )}
              {voice?.alias && (
                <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">
                  🗣️ "{voice.alias}"
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Widget palette (add buttons) - only show when selected */}
        {selected && (
          <div className="p-2 border-b border-gray-100 bg-gray-50/50">
            <WidgetPalette widgetCount={widgets.length} onAddWidget={handleAddWidget} />
          </div>
        )}

        {/* Content area with widgets */}
        <div className="p-3">
          <WidgetCanvas widgets={widgets} onWidgetsChange={handleWidgetsChange} />
        </div>

        {/* Footer with app info */}
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400">
          <span>App: {appId.slice(0, 8)}...</span>
          <span className="mx-2">•</span>
          <span>{widgets.length} widget{widgets.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* App Settings Modal */}
      <AppSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={currentSettings}
        onSave={handleSaveSettings}
        connectedScreens={connectedScreens}
        homeScreenId={connectedScreens.find((s) => s.isHome)?.id || id}
        appId={appId}
      />

      {/* Publish Panel */}
      <PublishPanel
        isOpen={showPublishPanel}
        onClose={() => setShowPublishPanel(false)}
        sourceNodeId={id}
        nodes={allNodes}
        edges={allEdges}
        boardId={urlBoardId || appId || id}
      />

      {/* Screen Voice Settings Modal */}
      <ScreenVoiceModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        config={voice}
        onSave={handleSaveVoiceConfig}
      />
    </div>
  );
});

AppScreenNode.displayName = 'AppScreenNode';
